/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Loader2, Clock, Zap, FileText, Sparkles, X, Rss, Search as SearchIcon, Globe, AlertTriangle, CheckCircle2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, formatDistanceToNow } from "date-fns";
import { LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";
import SourceLogsPanel from "@/components/sources/SourceLogsPanel";
import SourceAnalyticsPanel from "@/components/sources/SourceAnalyticsPanel";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";

const tenantDb = supabase as any;

interface NewsSource {
  id: string;
  topic: string;
  keywords: string[];
  language: string;
  supported_languages: string[];
  max_articles: number;
  is_active: boolean;
  last_fetched_at: string | null;
  fetch_count: number;
  created_at: string;
  feed_url: string | null;
  source_type: string;
  fetch_frequency_minutes: number;
  next_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  consecutive_failures: number;
  is_trial_demo?: boolean;
}

const trialDemoSources: NewsSource[] = [
  {
    id: "trial-demo-source-rss",
    topic: "Demo RSS: European technology",
    keywords: ["AI", "startups", "policy"],
    language: "en",
    supported_languages: ["en", "fr"],
    max_articles: 8,
    is_active: true,
    last_fetched_at: new Date(Date.now() - 1000 * 60 * 48).toISOString(),
    fetch_count: 14,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    feed_url: "https://example.com/demo-tech.xml",
    source_type: "rss",
    fetch_frequency_minutes: 360,
    next_run_at: new Date(Date.now() + 1000 * 60 * 90).toISOString(),
    last_status: "success",
    last_error: null,
    consecutive_failures: 0,
    is_trial_demo: true,
  },
  {
    id: "trial-demo-source-research",
    topic: "Demo AI research monitor",
    keywords: ["media automation", "publishing", "analytics"],
    language: "fr",
    supported_languages: ["fr"],
    max_articles: 5,
    is_active: true,
    last_fetched_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    fetch_count: 6,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    feed_url: null,
    source_type: "perplexity",
    fetch_frequency_minutes: 720,
    next_run_at: new Date(Date.now() + 1000 * 60 * 240).toISOString(),
    last_status: "success",
    last_error: null,
    consecutive_failures: 0,
    is_trial_demo: true,
  },
];

const FREQUENCY_OPTIONS = [
  { value: 5, label: "5 min" },
  { value: 15, label: "15 min" },
  { value: 60, label: "1 h" },
  { value: 360, label: "6 h" },
  { value: 720, label: "12 h" },
  { value: 1440, label: "24 h" },
];

export default function SourcesPage() {
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const trialMode = useTrialMode();
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [newKeywords, setNewKeywords] = useState("");
  const [newFeedUrl, setNewFeedUrl] = useState("");
  const [newSourceType, setNewSourceType] = useState<"rss" | "perplexity">("rss");
  const [newMax, setNewMax] = useState(5);
  const [newLanguage, setNewLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const [newSupportedLangs, setNewSupportedLangs] = useState<string[]>([]);
  const [newFrequency, setNewFrequency] = useState<number>(360);
  const [languageFilter, setLanguageFilter] = useState<string>("all");
  const [adding, setAdding] = useState(false);
  const [fetchingIds, setFetchingIds] = useState<Set<string>>(new Set());
  const [fetchingAll, setFetchingAll] = useState(false);
  const [tab, setTab] = useState("sources");

  useEffect(() => { if (user && currentCompanyId) fetchSources(); }, [currentCompanyId, user]);

  const fetchSources = async () => {
    const { data } = await tenantDb
      .from("news_sources")
      .select("*")
      .eq("company_id", currentCompanyId)
      .order("created_at", { ascending: false });
    const rows = (data as NewsSource[]) || [];
    setSources(trialMode.isReadOnly && rows.length === 0 ? trialDemoSources : rows);
    setLoading(false);
  };

  const addSource = async () => {
    if (!newTopic.trim() || !user || !currentCompanyId) return;
    if (trialMode.isReadOnly) {
      toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
      return;
    }
    if (newSourceType === "rss" && !newFeedUrl.trim()) return;
    setAdding(true);
    try {
      const keywords = newKeywords.split(",").map((k) => k.trim()).filter(Boolean);
      const { error } = await tenantDb.from("news_sources").insert({
        company_id: currentCompanyId,
        user_id: user.id,
        topic: newTopic.trim(),
        keywords,
        max_articles: newMax,
        language: newLanguage,
        supported_languages: newSupportedLangs,
        source_type: newSourceType,
        feed_url: newSourceType === "rss" ? newFeedUrl.trim() : null,
        fetch_frequency_minutes: newFrequency,
      });
      if (error) throw error;
      toast({ title: "Source ajoutée", description: `"${newTopic}" surveillée toutes les ${FREQUENCY_OPTIONS.find(f => f.value === newFrequency)?.label || newFrequency + 'min'}` });
      setNewTopic(""); setNewKeywords(""); setNewFeedUrl(""); setNewMax(5);
      setNewLanguage(DEFAULT_LANGUAGE); setNewSupportedLangs([]); setNewFrequency(360);
      setShowAdd(false);
      fetchSources();
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally { setAdding(false); }
  };

  const toggleSource = async (id: string, active: boolean) => {
    if (trialMode.isReadOnly || sources.find((source) => source.id === id)?.is_trial_demo) {
      toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
      return;
    }
    await tenantDb.from("news_sources").update({ is_active: active }).eq("id", id).eq("company_id", currentCompanyId);
    setSources((p) => p.map((s) => (s.id === id ? { ...s, is_active: active } : s)));
  };

  const deleteSource = async (id: string) => {
    if (trialMode.isReadOnly || sources.find((source) => source.id === id)?.is_trial_demo) {
      toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
      return;
    }
    if (!confirm("Supprimer cette source ?")) return;
    await tenantDb.from("news_sources").delete().eq("id", id).eq("company_id", currentCompanyId);
    setSources((p) => p.filter((s) => s.id !== id));
    toast({ title: "Source supprimée" });
  };

  const updateSourceField = async (id: string, patch: Partial<NewsSource>) => {
    if (trialMode.isReadOnly || sources.find((source) => source.id === id)?.is_trial_demo) {
      toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
      return;
    }
    await tenantDb.from("news_sources").update(patch).eq("id", id).eq("company_id", currentCompanyId);
    setSources((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const triggerFetch = async (sourceIds?: string[]) => {
    const ids = sourceIds || [];
    if (trialMode.isReadOnly) {
      toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" });
      return;
    }
    if (ids.length) setFetchingIds(new Set(ids)); else setFetchingAll(true);
    try {
      const { data, error } = await supabase.functions.invoke("auto-fetch-news", {
        body: { source_ids: ids.length ? ids : undefined, company_id: currentCompanyId, trigger: "manual", ignore_schedule: true },
      });
      if (error) throw error;
      toast({
        title: `${data?.totalImported ?? 0} article(s) importé(s)`,
        description: `${data?.results?.length || 0} source(s) traitée(s) en ${((data?.duration_ms || 0) / 1000).toFixed(1)}s`,
      });
      fetchSources();
    } catch (err: any) {
      toast({ title: "Échec de l'exécution", description: err.message, variant: "destructive" });
    } finally {
      setFetchingIds(new Set());
      setFetchingAll(false);
    }
  };

  const filteredSources = useMemo(() =>
    languageFilter === "all"
      ? sources
      : sources.filter((s) => s.language === languageFilter || (s.supported_languages || []).includes(languageFilter)),
    [sources, languageFilter]
  );

  const groupedByLanguage = useMemo(() => {
    const m = new Map<string, NewsSource[]>();
    for (const s of filteredSources) {
      const k = s.language || "?";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(s);
    }
    return Array.from(m.entries());
  }, [filteredSources]);

  const activeCount = filteredSources.filter((s) => s.is_active).length;
  const failingCount = filteredSources.filter((s) => s.last_status === "failed").length;

  const sourcesMini = useMemo(() => sources.map((s) => ({ id: s.id, topic: s.topic, language: s.language })), [sources]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Sources & Monitoring</h1>
          <p className="text-xs text-muted-foreground">
            {activeCount} active{activeCount !== 1 ? "s" : ""} · {failingCount} en échec · pipeline observable
            {trialMode.isReadOnly && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Demo data</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={languageFilter} onValueChange={setLanguageFilter}>
            <SelectTrigger className="h-9 w-[150px]">
              <Globe className="h-3.5 w-3.5 mr-1" />
              <SelectValue placeholder="Langue" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les langues</SelectItem>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => triggerFetch()} disabled={fetchingAll || sources.length === 0 || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
            {fetchingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            Tout lancer
          </Button>
          <Button size="sm" onClick={() => trialMode.isReadOnly ? toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" }) : setShowAdd(true)}>
            <Plus className="mr-2 h-4 w-4" /> Ajouter une source
          </Button>
        </div>
      </header>

      <div className="p-6">
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="sources">Sources</TabsTrigger>
            <TabsTrigger value="logs">Logs & Exécutions</TabsTrigger>
            <TabsTrigger value="analytics">Analytique</TabsTrigger>
          </TabsList>

          {/* SOURCES TAB */}
          <TabsContent value="sources" className="space-y-6">
            {showAdd && (
              <div className="rounded-lg border border-primary/30 bg-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold">Nouvelle source</h2>
                  </div>
                  <button onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setNewSourceType("rss")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${newSourceType === "rss" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  ><Rss className="h-3.5 w-3.5" /> Flux RSS</button>
                  <button
                    onClick={() => setNewSourceType("perplexity")}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${newSourceType === "perplexity" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  ><SearchIcon className="h-3.5 w-3.5" /> Recherche IA</button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Nom de la source</label>
                    <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder={newSourceType === "rss" ? "Ex: Reuters Tech" : "Ex: Intelligence artificielle"} />
                  </div>
                  {newSourceType === "rss" && (
                    <div>
                      <label className="text-xs font-medium mb-1 block">URL du flux RSS</label>
                      <Input value={newFeedUrl} onChange={(e) => setNewFeedUrl(e.target.value)} type="url" placeholder="https://..." />
                    </div>
                  )}
                  {newSourceType === "perplexity" && (
                    <div>
                      <label className="text-xs font-medium mb-1 block">Mots-clés (optionnel)</label>
                      <Input value={newKeywords} onChange={(e) => setNewKeywords(e.target.value)} placeholder="Gemini, deeptech (séparés par virgules)" />
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Langue principale</label>
                      <Select value={newLanguage} onValueChange={setNewLanguage}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Fréquence</label>
                      <Select value={String(newFrequency)} onValueChange={(v) => setNewFrequency(Number(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQUENCY_OPTIONS.map((f) => <SelectItem key={f.value} value={String(f.value)}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Max articles / collecte</label>
                      <Select value={String(newMax)} onValueChange={(v) => setNewMax(Number(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{[3, 5, 8, 10, 15, 20].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>Annuler</Button>
                  <Button size="sm" onClick={addSource} disabled={adding || !newTopic.trim() || (newSourceType === "rss" && !newFeedUrl.trim())}>
                    {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ajouter
                  </Button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-sm text-muted-foreground">Chargement...</div>
            ) : filteredSources.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Sparkles className="h-12 w-12 text-muted-foreground/40 mb-4" />
                <h3 className="text-lg font-medium">Aucune source configurée</h3>
                <Button className="mt-4" onClick={() => trialMode.isReadOnly ? toast({ title: "Trial workspace", description: TRIAL_READ_ONLY_MESSAGE, variant: "destructive" }) : setShowAdd(true)}><Plus className="mr-2 h-4 w-4" /> Ajouter une source</Button>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedByLanguage.map(([lang, list]) => {
                  const langInfo = LANGUAGES.find((l) => l.code === lang);
                  const groupIds = list.map((s) => s.id);
                  const groupBusy = groupIds.some((id) => fetchingIds.has(id));
                  return (
                    <div key={lang} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {langInfo?.flag} {langInfo?.label || lang} · {list.length} source{list.length > 1 ? "s" : ""}
                        </h3>
                        <Button size="sm" variant="outline" onClick={() => triggerFetch(groupIds)} disabled={groupBusy || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                          {groupBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                          Lancer ce groupe
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {list.map((source) => {
                          const busy = fetchingIds.has(source.id);
                          const nextRunLabel = source.next_run_at
                            ? new Date(source.next_run_at) > new Date()
                              ? `dans ${formatDistanceToNow(new Date(source.next_run_at))}`
                              : "imminente"
                            : "non planifiée";
                          return (
                            <div key={source.id} className={`rounded-lg border bg-card p-4 ${source.is_active ? "border-border" : "border-border/50 opacity-60"}`}>
                              <div className="flex items-center gap-4 flex-wrap">
                                <Switch checked={source.is_active} onCheckedChange={(c) => toggleSource(source.id, c)} disabled={trialMode.isReadOnly || source.is_trial_demo} />
                                <div className="flex-1 min-w-[200px]">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-sm font-semibold">{source.topic}</h3>
                                    <Badge variant={source.source_type === "rss" ? "default" : "secondary"} className="text-[10px] gap-1">
                                      {source.source_type === "rss" ? <><Rss className="h-2.5 w-2.5" /> RSS</> : <><SearchIcon className="h-2.5 w-2.5" /> IA</>}
                                    </Badge>
                                    {source.is_trial_demo && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Demo</Badge>}
                                    {source.last_status === "success" && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[10px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> OK</Badge>}
                                    {source.last_status === "partial" && <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30 text-[10px]">Partiel</Badge>}
                                    {source.last_status === "failed" && <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Échec ×{source.consecutive_failures}</Badge>}
                                  </div>
                                  {source.feed_url && <p className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-md">{source.feed_url}</p>}
                                  {source.last_error && (
                                    <p className="text-[10px] text-destructive mt-0.5 truncate max-w-md" title={source.last_error}>⚠ {source.last_error}</p>
                                  )}
                                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1">
                                    <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {source.fetch_count} runs</span>
                                    {source.last_fetched_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {format(new Date(source.last_fetched_at), "dd/MM HH:mm")}</span>}
                                    <span>Prochaine: {nextRunLabel}</span>
                                  </div>
                                </div>

                                <Select value={String(source.fetch_frequency_minutes || 360)} onValueChange={(v) => updateSourceField(source.id, { fetch_frequency_minutes: Number(v), next_run_at: null })} disabled={trialMode.isReadOnly || source.is_trial_demo}>
                                  <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>{FREQUENCY_OPTIONS.map((f) => <SelectItem key={f.value} value={String(f.value)} className="text-xs">{f.label}</SelectItem>)}</SelectContent>
                                </Select>

                                <Button size="sm" variant="outline" onClick={() => triggerFetch([source.id])} disabled={busy || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
                                  Fetch Now
                                </Button>
                                <button onClick={() => deleteSource(source.id)} disabled={trialMode.isReadOnly || source.is_trial_demo} className="text-muted-foreground hover:text-destructive p-1 disabled:opacity-40">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="logs">
            <SourceLogsPanel sources={sourcesMini} onRetry={(ids) => triggerFetch(ids)} />
          </TabsContent>

          <TabsContent value="analytics">
            <SourceAnalyticsPanel sources={sourcesMini} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
